
import { useMapStore } from "@/store/useMapStore";
import { CustomizerConfig } from "@/config/customizerConfig";

interface FramesTabProps {
  config: CustomizerConfig;
}

// Frame colors matching the actual preview rendering
const frameColors: Record<string, string> = {
  none: "transparent",
  black: "#111827",
  white: "#E5E7EB",
  natural: "#D4A574",
  walnut: "#5C3317",
};

const FramesTab = ({ config }: FramesTabProps) => {
  const { selectedOptions, selectOption, colorCustomization } = useMapStore();

  return (
    <div className="p-6 space-y-6">
      <h3
        className="text-lg mb-4"
        style={{ fontFamily: "var(--font-heading)", color: "var(--brand-text)" }}
      >
        Frame Selection
      </h3>

      <div className="grid grid-cols-3 gap-3">
        {config.frames.map((frame) => {
          const isSelected = selectedOptions.Frame === frame.name;
          const color = frameColors[frame.id] || frameColors.black;

          return (
            <div
              key={frame.id}
              onClick={() => selectOption("Frame", frame.name)}
              className="p-3 rounded-xl text-center cursor-pointer transition-colors"
              style={{
                fontFamily: "var(--font-body)",
                border: isSelected
                  ? "2px solid var(--sage-400)"
                  : "1px solid var(--brand-border)",
                backgroundColor: isSelected ? "var(--sage-50)" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = "var(--sage-300)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = "var(--brand-border)";
                }
              }}
            >
              {/* Miniature framed art preview */}
              <div className="w-full aspect-[3/4] rounded mb-2 flex items-center justify-center"
                style={{
                  backgroundColor: "#F5F0EB",
                }}
              >
                {frame.id === "none" ? (
                  /* No frame: just a baseboard with map placeholder */
                  <div
                    className="w-[70%] h-[80%] rounded-sm"
                    style={{
                      backgroundColor: colorCustomization.baseBoardColor,
                      border: "1px dashed #D1D5DB",
                    }}
                  >
                    <div
                      className="w-full h-[65%]"
                      style={{ backgroundColor: colorCustomization.mapColor, opacity: 0.3 }}
                    />
                  </div>
                ) : (
                  /* Framed: frame border around a baseboard with map */
                  <div
                    className="w-[70%] h-[80%] rounded-sm"
                    style={{
                      border: `4px solid ${color}`,
                      boxShadow: `
                        inset 0 0 0 0.5px rgba(255,255,255,0.2),
                        0 1px 3px rgba(0,0,0,0.12)
                      `,
                    }}
                  >
                    <div
                      className="w-full h-full"
                      style={{ backgroundColor: colorCustomization.baseBoardColor }}
                    >
                      <div
                        className="w-full h-[65%]"
                        style={{ backgroundColor: colorCustomization.mapColor, opacity: 0.3 }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Frame name */}
              <span
                className={`text-xs ${isSelected ? "font-medium" : ""}`}
                style={{ color: "var(--brand-text)" }}
              >
                {frame.id === "none" ? "No Frame" : frame.name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FramesTab;
